// The Messages tab on the student and instructor dashboards (#333, in-app messaging).
//
// The tab label carries the reader's unread count (MS-3), loaded with the dashboard from
// GET /conversations. Opening a thread marks it read; the Messages panel then reports the new total.
import React from 'react';
import { api, isSignInExpired } from './api';

// The dashboard tab that lists conversations. The new message email links to `?tab=messages`
// (MESSAGES_TAB in backend/src/lib/messageEmails.js).
export const MESSAGES_TAB = 'messages';
const MESSAGES_LABEL = 'Messages';

// 'Messages' with no unread messages, 'Messages (3)' with three.
export function messagesTabLabel(unreadTotal) {
  const count = Number(unreadTotal) || 0;
  return count > 0 ? `${MESSAGES_LABEL} (${count})` : MESSAGES_LABEL;
}

// The role's tab list with the unread count on the Messages tab. Every other tab is unchanged.
export function withMessagesLabel(tabs, unreadTotal) {
  return tabs.map((tab) => (tab.id === MESSAGES_TAB ? { ...tab, label: messagesTabLabel(unreadTotal) } : tab));
}

// The tab a dashboard link names (`#/dashboard-student?tab=messages`), or null when the address
// names none or a tab this role does not have.
export function tabFromHash(hash, tabs) {
  const text = String(hash ?? '');
  const q = text.indexOf('?');
  if (q === -1) return null;
  const id = new URLSearchParams(text.slice(q + 1)).get('tab');
  return tabs.some((tab) => tab.id === id) ? id : null;
}

// The signed-in reader's unread total, loaded once with the dashboard. The setter takes the new
// total the Messages panel reports. A failed load keeps the plain label and logs the error; the
// Messages panel itself says when the conversations could not load (#507).
export function useUnreadMessages() {
  const [unreadTotal, setUnreadTotal] = React.useState(0);
  React.useEffect(() => {
    let open = true;
    api.get('/conversations')
      .then((data) => { if (open) setUnreadTotal(data?.unreadTotal || 0); })
      .catch((err) => {
        // An ended sign-in already took them to the login page with its own notice (#502). Any
        // other failure, a server problem while renewing the sign-in included, is logged (#507).
        if (isSignInExpired(err)) return;
        console.error('[messages] could not load the unread count', err?.status ?? err);
      });
    return () => { open = false; };
  }, []);
  return [unreadTotal, setUnreadTotal];
}
